import React, { useEffect, useRef, useState } from 'react';
import SingleImageField from './Fields/SingleImageField';
import MultipleImageField from './Fields/MultiImageField';
import TextField from './Fields/TextField';
import NumberField from './Fields/NumberField';
import EmailField from './Fields/EmailField';
import TelephoneField from './Fields/TelephoneField';
import DateField from './Fields/DateField';
import CheckboxField from './Fields/CheckboxField';
import ListField from './Fields/ListField';
import ObjectListField from './Fields/ObjectListField';
import SelectionField from './Fields/SelectionField';
import LongTextField from './Fields/LongTextField';
import CalculatedField from './Fields/CalculatedField';

import { validateField as validateFieldAPI } from '../ServiceRequest';

type FieldProps = {
  readOnly: boolean;
  value: string | string[];
  field: any;
  form: React.RefObject<HTMLFormElement>;
  invalid?: boolean;
  submitted?: boolean;

  
  templateName: string;
  documentId?: string;
};

const SIMPLE_TYPES_FOR_SERVER_VALIDATE = new Set([
  'text', 'email', 'telephone', 'date', 'select', 'longText', 'multiple' 
]);

const FormField: React.FC<FieldProps> = ({
  readOnly,
  value,
  field,
  form,
  invalid,
  submitted = false,
  templateName,
  documentId
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [serverErr, setServerErr] = useState<string | null>(null);
  const isCheckboxGroup = field.type === 'multiple' && field.htmlType === 'checkbox';

  
  const skipCentralValidation =
    field.type === 'number' ||
    field.type === 'image' ||
    field.type === 'objectList' ||
    field.type === 'frontend_calculated' ||
    isCheckboxGroup;

  useEffect(() => {
    if (skipCentralValidation || readOnly) return;

    const root = containerRef.current;
    if (!root) return;

    
    const el: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null =
      root.querySelector(`input[name="${CSS.escape(field.name)}"], select[name="${CSS.escape(field.name)}"], textarea[name="${CSS.escape(field.name)}"]`);

    if (!el) return;

    let pasteTimer: number | null = null;
    const clearPasteTimer = () => { if (pasteTimer) window.clearTimeout(pasteTimer); pasteTimer = null; };

    const handleChange = () => {
      
      if (serverErr) setServerErr(null);
    };

    const runServerValidation = async () => {
      const rawValue = (el as any).value ?? '';
      if (!rawValue || field?.nullable && rawValue === 'N/A') {
        setServerErr(null);
        return;
      }
      try {
        const res: any = await validateFieldAPI(
          templateName,
          field.name,
          String(rawValue),
          documentId
        );

        if (res?.valid) {
          setServerErr(null);
          if (res?.normalizedValue != null && res.normalizedValue !== rawValue) {
            
            (el as any).value = res.normalizedValue;
            el.dispatchEvent(new Event('input', { bubbles: true }));

            
            const ev = new CustomEvent('calc:accepted', {
              bubbles: true,
              detail: { raw: res.normalizedValue, display: res.normalizedValue }
            });
            el.dispatchEvent(ev);
          }
        } else {
          const msg = res?.errors?.[0]?.message || 'Invalid value';
          setServerErr(msg);
        }
      } catch {
        
      }
    };

    const onBlur = () => runServerValidation();
    const onPaste = () => {
      clearPasteTimer();
      pasteTimer = window.setTimeout(runServerValidation, 0);
    };

    el.addEventListener('change', handleChange);
    el.addEventListener('blur', onBlur, true);
    el.addEventListener('paste', onPaste);

    return () => {
      clearPasteTimer();
      el.removeEventListener('change', handleChange);
      el.removeEventListener('blur', onBlur, true);
      el.removeEventListener('paste', onPaste);
    };
  }, [skipCentralValidation, readOnly, field?.name, field?.nullable, templateName, documentId, serverErr]);

  
  const renderServerError = () =>
    serverErr ? (
      <div style={{ marginTop: 6, fontSize: 12, color: '#E53E3E' }}>
        {serverErr}
      </div>
    ) : null;

  let node: React.ReactNode = null;

  switch (field.type) {
    case 'text':
      node = <TextField field={field} value={value as string} readOnly={readOnly} invalid={!!invalid} submitted={submitted} />;
      break;

    case 'email':
      node = <EmailField field={field} value={value as string} readOnly={readOnly} invalid={!!invalid} submitted={submitted} />;
      break;

    case 'number':
      
      node = (
        <NumberField
          field={field}
          value={value as string}
          readOnly={readOnly}
          invalid={!!invalid}
          submitted={submitted}
          templateName={templateName}
          documentId={documentId}
        />
      );
      break;

    case 'telephone':
      node = <TelephoneField field={field} value={value as string} readOnly={readOnly} invalid={!!invalid} submitted={submitted} />;
      break;

    case 'longText':
      node = <LongTextField field={field} value={value as string} readOnly={readOnly} invalid={!!invalid} submitted={submitted} />;
      break;

    case 'select':
      node = <SelectionField field={field} value={value as string} readOnly={readOnly} invalid={!!invalid} submitted={submitted} />;
      break;

    case 'date':
      node = <DateField field={field} value={value as string} readOnly={readOnly} invalid={!!invalid} submitted={submitted} />;
      break;

    case 'image':
      node = !field.multiple
        ? <SingleImageField field={field} value={value} invalid={!!invalid} />
        : <MultipleImageField field={field} value={value} invalid={!!invalid} />;
      break;

    case 'multiple':
      node = isCheckboxGroup
        ? <CheckboxField field={field} value={value} invalid={!!invalid} />
        : <ListField field={field} value={value} invalid={!!invalid} />;
      break;

    case 'list':
      node = <ListField field={field} value={value} invalid={!!invalid} />;
      break;

    case 'objectList':
      node = <ObjectListField field={field} value={value} invalid={!!invalid} />;
      break;

    case 'frontend_calculated':
      node = (
        <CalculatedField
          field={field}
          value={(value as string) ?? ''}
          readOnly={true}
          invalid={!!invalid}
          submitted={submitted}
        />
      );
      break;

    default:
      node = null;
  }

  
  return (
    <div ref={containerRef} data-form-field={field?.name}>
      {node}
      {/* helper per errore server (solo se centralizzato) */}
      {!skipCentralValidation && renderServerError()}
    </div>
  );
};

export default FormField;
